import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { listCaptures } from "../../api/captures";
import { getCaptureById } from "../../api/captures/capture";
import { runExport } from "../../api/exports";
import { getCaptureFlowTests } from "../../api/flowTests/FlowTest";
import { useTranslationHook } from "../../hooks";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../hooks/useFormStyles";
import { ExportDataRequestSchema } from "../../types/model/export";
import { ExportRow } from "../../types/model/export/ExportRow";
import { FlowTestResponse } from "../../types/model/flowTest";
import { PaginatedAutocomplete } from "../General/PaginatedAutocomplete";
import FlowTestCard, { FlowTestInfo } from "./FlowTestCard";

interface RunExportDialogProps {
  open: boolean;
  exportRow: ExportRow | null;
  onClose: () => void;
  onSuccess: () => void;
}

type ScopeFormValues = {
  captureIds?: number[];
  captureId?: number;
};

const RunExportDialog = ({ open, exportRow, onClose, onSuccess }: RunExportDialogProps) => {
  const { t } = useTranslationHook();
  const handleError = useErrorHandler();
  const formStyles = useFormStyles(true);

  const isFlowTestScope = exportRow?.scopeColumn === "flow_test_id";
  const isCaptureScope = exportRow?.scopeColumn === "capture_id";

  const methods = useForm<ScopeFormValues>({
    defaultValues: { captureIds: [], captureId: undefined },
  });

  const selectedCaptureId = methods.watch("captureId");
  const selectedCaptureIds = methods.watch("captureIds") ?? [];

  const [flowTests, setFlowTests] = useState<FlowTestResponse[]>([]);
  const [selectedFlowTests, setSelectedFlowTests] = useState<FlowTestResponse[]>([]);
  const [loadingFlowTests, setLoadingFlowTests] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    methods.reset({ captureIds: [], captureId: undefined });
    setFlowTests([]);
    setSelectedFlowTests([]);
    setErrorMessage(null);
  }, [open]);

  useEffect(() => {
    if (!isFlowTestScope || !selectedCaptureId) {
      setFlowTests([]);
      setSelectedFlowTests([]);
      return;
    }

    const fetchFlowTests = async () => {
      setLoadingFlowTests(true);
      try {
        const data = await getCaptureFlowTests(selectedCaptureId);
        setFlowTests(data);
      } catch (error) {
        handleError(error);
      } finally {
        setLoadingFlowTests(false);
      }
    };

    fetchFlowTests();
  }, [selectedCaptureId, isFlowTestScope]);

  const fetchCaptures = useCallback(
    (search: string, offset: number, limit: number) =>
      listCaptures(undefined, undefined, search, undefined, limit, offset, false),
    [],
  );

  const fetchOneCapture = useCallback((id: number) => getCaptureById(id), []);

  const selectedCount = isCaptureScope ? selectedCaptureIds.length : selectedFlowTests.length;
  const canRun = selectedCount > 0;

  const handleSubmit = async () => {
    if (!exportRow) return;

    let scopeIds: number[] = [];

    if (isCaptureScope) {
      scopeIds = methods.getValues("captureIds") ?? [];
    } else if (isFlowTestScope) {
      scopeIds = selectedFlowTests.map((ft) => ft.id);
    }

    const parsed = ExportDataRequestSchema.safeParse({ scopeIds });
    if (!parsed.success || scopeIds.length === 0) {
      setErrorMessage(t("exports.dialog.select-at-least-one"));
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);
    try {
      await runExport(exportRow.id, parsed.data);
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      setErrorMessage(t("generic.errors.something-went-wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteFlowTest = (flowTestId: number) => {
    setSelectedFlowTests((prev) =>
      prev.filter((item) => item.id !== flowTestId),
    );
  };

  const scopeKind = isCaptureScope ? "capture" : "flow-test";
  const quantityKind = selectedCount === 1 ? "singular" : "plural";

  let warningMessage = t(`exports.dialog.warning-${scopeKind}-${quantityKind}`);
  if (selectedCount > 1) {
    warningMessage = warningMessage.replace("{{count}}", String(selectedCount));
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography variant="h6">{t("exports.dialog.run-export")}</Typography>

            {exportRow && (
              <Chip label={exportRow.name} size="small" color="secondary" sx={{ fontWeight: 600 }} />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary">
            {t("exports.dialog.description")}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mt: 1 }}>
          <FormProvider {...methods}>
            <Box component="form" sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
              {isCaptureScope && (
                <PaginatedAutocomplete
                  name="captureIds"
                  label={t("exports.dialog.select-captures")}
                  fetchFn={fetchCaptures}
                  fetchOneFn={fetchOneCapture}
                  isEditing
                  isSaving={submitting}
                  formStyles={formStyles}
                  multiple
                />
              )}

              {isFlowTestScope && (
                <>
                  <PaginatedAutocomplete
                    name="captureId"
                    label={t("exports.dialog.select-capture")}
                    fetchFn={fetchCaptures}
                    fetchOneFn={fetchOneCapture}
                    isEditing
                    isSaving={submitting}
                    formStyles={formStyles}
                  />

                  <Autocomplete
                    multiple
                    options={flowTests}
                    loading={loadingFlowTests}
                    disabled={!selectedCaptureId}
                    value={selectedFlowTests}
                    onChange={(_, newValue) => setSelectedFlowTests(newValue)}
                    getOptionLabel={(ft) =>
                      ft.startDate
                        ? new Intl.DateTimeFormat("pt-PT").format(new Date(ft.startDate))
                        : `#${ft.id}`
                    }
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderTags={() => null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("exports.dialog.select-flow-tests")}
                        placeholder={
                          selectedCaptureId ? undefined : t("exports.dialog.select-capture-first")
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <FlowTestInfo flowTest={option} />
                      </li>
                    )}
                  />

                  {selectedFlowTests.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        maxHeight: 320,
                        overflowY: "auto",
                        pr: 0.5,
                      }}
                    >
                      {selectedFlowTests.map((ft) => (
                        <FlowTestCard
                          key={ft.id}
                          flowTest={ft}
                          onDelete={() => handleDeleteFlowTest(ft.id)}
                        />
                      ))}
                    </Box>
                  )}
                </>
              )}

              {errorMessage && (
                <Typography color="error" variant="body2">
                  {errorMessage}
                </Typography>
              )}
            </Box>
          </FormProvider>

          {canRun && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {warningMessage}
            </Alert>
          )}
        </Paper>
      </DialogContent>

      <DialogActions>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <Button onClick={onClose} disabled={submitting}>
            {t("generic.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting || !canRun}>
            {t("exports.dialog.run")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RunExportDialog;